import { useEffect, useState } from 'react';
import { LuTriangleAlert } from 'react-icons/lu';
import { toast } from 'sonner';

import { Account, GUID, PortfolioEngineAccount } from '@ndvr/portfolio-api';

import { useInferredPositions } from '@/cip/api/position';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { calculateTotalWeight } from '@/views/portfolio-engine/allocation-builder/shared-utils';
import { useBatchOnboard } from '@/views/portfolio/account-queries';

import { useTlhCandidates } from './api/use-tlh-candidates';
import { WizardAccountsList, WizardExcludedAccount } from './components/WizardAccountsList';
import { WizardStepNav } from './components/WizardStepNav';
import { buildOnboardSubmission } from './onboard-submission';
import { FinalReviewStep } from './steps/FinalReviewStep';
import { ManagementStep } from './steps/ManagementStep';
import { TargetAllocationStep } from './steps/TargetAllocationStep';
import { TaxLossHarvestingStep } from './steps/TaxLossHarvestingStep';
import {
    INITIAL_ONBOARDING_WIZARD_STATE,
    ONBOARDING_WIZARD_STEP_ORDER,
    OnboardingStepProps,
    OnboardingWizardStep,
} from './types';
import { buildWeightMap } from './utils';

interface Props {
    accounts: PortfolioEngineAccount[];
    onClose: () => void;
}

const STEP_COMPONENTS: Record<OnboardingWizardStep, React.ComponentType<OnboardingStepProps>> = {
    [OnboardingWizardStep.MANAGEMENT]: ManagementStep,
    [OnboardingWizardStep.TARGET_ALLOCATION]: TargetAllocationStep,
    [OnboardingWizardStep.TAX_LOSS_HARVESTING]: TaxLossHarvestingStep,
    [OnboardingWizardStep.FINAL_REVIEW]: FinalReviewStep,
};

const NO_INFERRED_POSITIONS_REASON =
    'No inferred positions were returned for this account. Without a current portfolio snapshot, there is no target allocation to submit, so this account cannot be onboarded at this time.';

const NoOnboardableAccountsWarning = () => (
    <div className="flex h-full items-center justify-center">
        <div className="border-cocoa-200 flex max-w-xl flex-col items-center rounded-lg border px-8 py-10 text-center">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                <LuTriangleAlert className="h-6 w-6" />
            </div>
            <h3 className="text-cocoa-1000 text-2xl font-medium">No accounts can be onboarded</h3>
            <div className="text-cocoa-600 mt-3 space-y-2 text-sm leading-relaxed">
                <p>
                    None of the selected accounts returned inferred positions, so there is no target allocation to
                    submit.
                </p>
                <p>Accounts with no inferred positions are excluded automatically.</p>
            </div>
        </div>
    </div>
);

export const OnboardingWizardModal = ({ accounts: initialAccounts, onClose }: Props) => {
    const [currentStep, setCurrentStep] = useState<OnboardingWizardStep>(OnboardingWizardStep.MANAGEMENT);
    const [state, setState] = useState(INITIAL_ONBOARDING_WIZARD_STATE);
    const [accounts, setAccounts] = useState(initialAccounts);
    const [excludedAccounts, setExcludedAccounts] = useState<WizardExcludedAccount[]>([]);
    const tenantGUID = accounts[0]?.tenantGUID;

    const excludeAccount = (accountGUID: GUID<Account>) => {
        const account = accounts.find((a) => a.accountGUID === accountGUID);
        if (!account) return;

        setAccounts((prev) => prev.filter((a) => a.accountGUID !== accountGUID));
        setExcludedAccounts((prev) => [...prev, { account, canRestore: true }]);
    };

    const includeAccount = (accountGUID: GUID<Account>) => {
        const excludedAccount = excludedAccounts.find(
            (entry) => entry.account.accountGUID === accountGUID && entry.canRestore,
        );
        if (!excludedAccount) return;

        setExcludedAccounts((prev) => prev.filter((entry) => entry.account.accountGUID !== accountGUID));
        setAccounts((prev) => [...prev, excludedAccount.account]);
    };

    const disabledStepReasons: Partial<Record<OnboardingWizardStep, string>> = state.management.taxLossHarvesting
        ? {}
        : {
              [OnboardingWizardStep.TAX_LOSS_HARVESTING]:
                  'Enable Tax Loss Harvesting in Management to configure replacements.',
          };
    const activeStepOrder = ONBOARDING_WIZARD_STEP_ORDER.filter((step) => !disabledStepReasons[step]);
    const currentIndex = activeStepOrder.indexOf(currentStep);
    const isLastStep = currentIndex === activeStepOrder.length - 1;
    const StepComponent = STEP_COMPONENTS[currentStep];

    const hasIncludedAccounts = accounts.length > 0;
    const noOnboardableAccountsDueToNoPositions =
        !hasIncludedAccounts && excludedAccounts.length > 0 && excludedAccounts.every((entry) => !entry.canRestore);

    const { data: inferred = [], isLoading: isInferredLoading } = useInferredPositions(
        accounts.map((a) => a.accountGUID),
    );

    const allWeightsValid =
        !isInferredLoading &&
        accounts.every((account) => {
            const overrides = state.weightOverrides[account.accountGUID];
            return (
                overrides !== undefined &&
                calculateTotalWeight(Object.values(overrides).map((w) => ({ weight: w }))) === 100
            );
        });

    const canAdvance: Record<OnboardingWizardStep, boolean> = {
        [OnboardingWizardStep.MANAGEMENT]: hasIncludedAccounts && state.portfolioBoostEnabled,
        [OnboardingWizardStep.TARGET_ALLOCATION]: hasIncludedAccounts && allWeightsValid,
        [OnboardingWizardStep.TAX_LOSS_HARVESTING]: hasIncludedAccounts,
        [OnboardingWizardStep.FINAL_REVIEW]: hasIncludedAccounts,
    };
    const nextDisabled = !canAdvance[currentStep];

    useEffect(() => {
        if (isInferredLoading || accounts.length === 0) return;

        const inferredByGuid = new Map(inferred.map((portfolio) => [portfolio.portfolioGuid, portfolio]));

        setState((prev) => {
            const updates: Record<string, Record<string, string>> = {};
            for (const account of accounts) {
                if (prev.weightOverrides[account.accountGUID]) continue;
                const portfolio = inferredByGuid.get(account.accountGUID);
                if (!portfolio || (portfolio.positions ?? []).length === 0) continue;
                updates[account.accountGUID] = buildWeightMap(portfolio);
            }
            if (Object.keys(updates).length === 0) return prev;
            return { ...prev, weightOverrides: { ...prev.weightOverrides, ...updates } };
        });

        const accountsWithoutInferredPositions = accounts.filter(
            (account) => (inferredByGuid.get(account.accountGUID)?.positions.length ?? 0) === 0,
        );
        if (accountsWithoutInferredPositions.length === 0) return;

        const excludedAccountGUIDs = new Set(accountsWithoutInferredPositions.map((account) => account.accountGUID));
        setAccounts((prev) => prev.filter((account) => !excludedAccountGUIDs.has(account.accountGUID)));
        setExcludedAccounts((prev) => {
            const existing = new Set(prev.map((entry) => entry.account.accountGUID));
            return [
                ...prev,
                ...accountsWithoutInferredPositions
                    .filter((account) => !existing.has(account.accountGUID))
                    .map((account) => ({
                        account,
                        reason: NO_INFERRED_POSITIONS_REASON,
                        canRestore: false,
                    })),
            ];
        });
    }, [accounts, inferred, isInferredLoading]);

    const { data: tlhHoldings, isLoading: isTlhLoading } = useTlhCandidates(inferred);
    const { mutate: batchOnboard, isPending: isSubmitting } = useBatchOnboard(tenantGUID, { onSuccess: onClose });
    const submit = () => {
        try {
            batchOnboard(buildOnboardSubmission({ accounts, state, inferred, tlhHoldings }));
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Failed to build onboarding request');
        }
    };

    const goNext = () => {
        if (isLastStep) {
            submit();
            return;
        }
        setCurrentStep(activeStepOrder[currentIndex + 1]);
    };

    return (
        <Dialog open onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="flex h-[90vh] w-[95vw] max-w-[1400px] flex-col gap-0 overflow-hidden p-0">
                <div className="flex min-h-0 flex-1">
                    <aside className="border-cocoa-200 bg-cocoa-50/40 flex w-72 shrink-0 flex-col overflow-hidden border-r">
                        <div className="px-4 py-4">
                            <DialogTitle className="text-cocoa-1000 font-heading text-xl">Onboarding</DialogTitle>
                            <DialogDescription className="sr-only">
                                Configure management settings, target allocation, and tax loss harvesting replacements
                                before onboarding the selected accounts.
                            </DialogDescription>
                        </div>
                        <WizardStepNav
                            currentStep={currentStep}
                            activeStepOrder={activeStepOrder}
                            disabledStepReasons={disabledStepReasons}
                            onSelectStep={setCurrentStep}
                        />
                        <div className="min-h-0 flex-1 overflow-auto">
                            <WizardAccountsList
                                accounts={accounts}
                                excludedAccounts={excludedAccounts}
                                onExcludeAccount={excludeAccount}
                                onIncludeAccount={includeAccount}
                            />
                        </div>
                    </aside>
                    <div className="flex min-w-0 flex-1 flex-col">
                        <div className="min-h-0 flex-1 overflow-auto px-8 py-6">
                            {noOnboardableAccountsDueToNoPositions ? (
                                <NoOnboardableAccountsWarning />
                            ) : (
                                <StepComponent
                                    accounts={accounts}
                                    state={state}
                                    setState={setState}
                                    inferredPositions={inferred}
                                    isInferredPositionsLoading={isInferredLoading}
                                    tlhHoldings={tlhHoldings}
                                    isTlhHoldingsLoading={isTlhLoading}
                                />
                            )}
                        </div>
                        <footer className="flex items-center justify-between px-8 py-4">
                            {noOnboardableAccountsDueToNoPositions ? (
                                <>
                                    <span />
                                    <Button onClick={onClose}>Close</Button>
                                </>
                            ) : (
                                <>
                                    <Button variant="ghost" onClick={onClose}>
                                        Cancel
                                    </Button>
                                    <div className="flex gap-2">
                                        <TooltipProvider>
                                            <Tooltip
                                                open={
                                                    currentStep === OnboardingWizardStep.TARGET_ALLOCATION &&
                                                    !allWeightsValid
                                                        ? undefined
                                                        : false
                                                }
                                            >
                                                <TooltipTrigger asChild>
                                                    <span>
                                                        <Button
                                                            onClick={goNext}
                                                            disabled={
                                                                nextDisabled ||
                                                                (isLastStep &&
                                                                    (isSubmitting || isInferredLoading || isTlhLoading))
                                                            }
                                                        >
                                                            {isLastStep && isSubmitting
                                                                ? 'Submitting…'
                                                                : isLastStep
                                                                  ? 'Submit'
                                                                  : 'Next'}
                                                        </Button>
                                                    </span>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    Target allocation weights must total 100%
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </div>
                                </>
                            )}
                        </footer>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};
